import * as React from 'react'
import * as formik from 'formik'
import styled from 'styled-components'

import * as plume from '@ulule/owl-kit-components/next'
import * as model from '@owl-nest/models'
import { t, tp } from '@owl-nest/localize'

import * as UFE from '../../../../UFE'

import { useStep, getStepIndex, getStepProgress, Step } from '../useStep'
import { Progress } from '../components/Progress'
import { Coaching } from '../components/Coaching'
import { Actions } from '../components/Actions'
import { Tip } from '../components/Tip'
import { Buttons } from '../components/Buttons'

import { Form, SolidContent } from './styles'

type ProjectTypeProps = {
  project: model.project.ProjectForOwner
}

export function ProjectType({ project }: ProjectTypeProps): React.ReactElement<any> {
  const { setStepValue } = useStep(project)

  return (
    <>
      <Progress
        progress={getStepProgress(Step.PROJECT_TYPE)}
        step={getStepIndex(Step.PROJECT_TYPE)}
        name={t('Your project')}
      />
      <Coaching project={project}>
        {t('Welcome to Ulule! Here’s the formula that I think would be best for you:')}
      </Coaching>
      <formik.Formik
        initialValues={{
          type: project.type,
        }}
        onSubmit={(values) => {
          setStepValue({ type: values.type })
        }}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {(formikBag) => {
          return (
            <StyledForm onSubmit={formikBag.handleSubmit}>
              <SolidContent>
                <formik.Field name="type">
                  {({ field }: formik.FieldProps<string>) => {
                    return (
                      <plume.RadioGroupField type="big">
                        <plume.RadioField
                          name={field.name}
                          value="project"
                          checked={field.value === 'project'}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          key="project"
                          label={
                            <>
                              {t('Reward-based fundraising')}
                              <plume.illustrations.twoToned.BookAndStuff size={50} />
                            </>
                          }
                          description={t(
                            'You offer rewards in exchange for financial support, with a minimum goal to reach within a given time frame.',
                          )}
                        />
                        <plume.RadioField
                          name={field.name}
                          value="donation"
                          checked={field.value === 'donation'}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          key="donation"
                          label={
                            <>
                              {t('Donation-based fundraising')}
                              <plume.illustrations.twoToned.Coins size={50} />
                            </>
                          }
                          description={t(
                            'You receive one-time or monthly donations (without any reward). Having a goal and deadline are optional.',
                          )}
                        />
                        <plume.RadioField
                          name={field.name}
                          value="membership"
                          checked={field.value === 'membership'}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          key="membership"
                          label={
                            <>
                              {tp('Memberships', 'Project type')}
                              <plume.illustrations.twoToned.Community size={50} />
                            </>
                          }
                          description={t(
                            'You receive monthly support through memberships or donations from your community, with or without compensation.',
                          )}
                        />
                      </plume.RadioGroupField>
                    )
                  }}
                </formik.Field>
              </SolidContent>

              <Actions>
                <Tip>{t('Nothing final, you can edit this information later.')}</Tip>
                <Buttons>
                  <plume.LinkAsButton kind="secondary" type="button" onClick={() => setStepValue()}>
                    {t('Skip')}
                  </plume.LinkAsButton>
                  <plume.Button kind="primary">{t('Next')}</plume.Button>
                </Buttons>
              </Actions>
            </StyledForm>
          )
        }}
      </formik.Formik>
    </>
  )
}

const StyledForm = styled(Form)`
  ${plume.RadioGroupField} {
    max-width: 462px;

    ${plume.illustrations.Illustration} {
      position: absolute;
      top: 25%;
      right: 13px;
    }
  }
`
